import { Controller } from "stimulus";
import { EventGeckoChartRendered, EventPriceLoaded, EventTooltipLoaded } from "../../events";
import { throttle } from "../../util";
import { createPopper } from "@popperjs/core";
import { useClickOutside } from "stimulus-use";
import { loadFlatpickr } from "../../util/load_package";

const _SECTION_SCROLL_OFFSET_PIXELS = 60;
const _SECTION_SCROLL_THRESHOLD_PIXELS = 200;

export default class extends Controller {
  static targets = ["tickerTypeItem", "spotTable", "perpetualsTable", "futuresTable", "exchangeTabs", "navAnchor", "exportButton", "exportDropdownMenu", "calendarButton"];

  calendarChartInstance = null;
  isCalendarValid = false;

  connect() {
    useClickOutside(this, { element: this.exportButtonTarget });

    this.isSpotMarket = this.element.dataset.marketType === "spot";
    this.activeMarketTab = this.isSpotMarket ? "spot" : "perpetuals";

    this.refreshAnchor();

    window.addEventListener("scroll", throttle(this._handleNavigationHighlight.bind(this), 100));
    window.addEventListener(EventGeckoChartRendered, (e) => {
      // Only the normalized volumes/open interest charts have a calendar button.
      // Only one chart will render, based on whether exchange is spot/futures respectively.
      if (e.target.dataset.chartType === "exchange:normalizedVolumes" || e.target.dataset.chartType === "exchange:openInterest") {
        this.calendarChartInstance = e.detail.chart;
        this.refreshCalendarSettings();
      }
    });
  }

  refreshAnchor() {
    let anchor = "spot";
    if (!this.isSpotMarket) {
      anchor = window.location.hash.substring(1) || "perpetuals";
    }

    let anchoredTab = this.tickerTypeItemTargets.find(x => x.dataset.key === anchor);
    this.handleTickerType(anchoredTab);
  }

  refreshActiveMarket() {
    requestAnimationFrame(() => {
      if (this.activeMarketTab === "perpetuals") {
        this.futuresTableTarget.classList.add("tw-hidden");
        this.perpetualsTableTarget.classList.remove("tw-hidden");
        this.perpetualsTableTarget.dispatchEvent(new CustomEvent(EventTooltipLoaded, { bubbles: true }));
      } else if (this.activeMarketTab === "futures") {
        this.perpetualsTableTarget.classList.add("tw-hidden");
        this.futuresTableTarget.classList.remove("tw-hidden");
        this.futuresTableTarget.dispatchEvent(new CustomEvent(EventTooltipLoaded, { bubbles: true }));
      }

      window.dispatchEvent(new CustomEvent(EventPriceLoaded, { bubbles: true }));
    });
  }

  handleTickerType(e) {
    let target = e?.currentTarget || e
    if (!target || this.isSpotMarket || target.dataset.key === "spot") {
      return;
    }

    if (e !== target && target.dataset.key === "perpetuals") {
      e.preventDefault();
    }

    this.tickerTypeItemTargets.forEach(target => target.classList.remove("selected"));
    target.classList.add("selected");

    this.activeMarketTab = target.dataset.key;
    this.refreshActiveMarket();

    if (target.dataset.key === "perpetuals") {
      history.replaceState("", document.title, window.location.pathname + window.location.search);
    } else {
      history.replaceState(undefined, undefined, `#${this.activeMarketTab}`);
    }
  }

  reloadMarkets(e) {
    if (this.isMarketsLoading === true) {
      return;
    }

    let tableTarget = this[`${this.activeMarketTab}TableTarget`];
    let buttonTarget = tableTarget.querySelector(".pair-targets-button");

    tableTarget.querySelector(".markets-loading").classList.remove("!tw-hidden")
    buttonTarget.disabled = true;

    this.isMarketsLoading = true;

    fetch(e.currentTarget.dataset.url)
      .then(response => response.text())
      .then(html => {
        tableTarget.outerHTML = html;
        this.refreshActiveMarket();
      })
      .finally(() => this.isMarketsLoading = false);
  }

  scrollToAnchor(e) {
    let target = this.navAnchorTargets.filter(x => x.dataset.key === e.currentTarget.id)[0];
    if (!target) {
      return;
    }

    const top = target.getBoundingClientRect().top + window.scrollY - _SECTION_SCROLL_OFFSET_PIXELS;
    window.scrollTo({ top, behavior: "smooth" });
  }

  _handleNavigationHighlight() {
    let previousElement = this.exchangeTabsTarget.querySelector(".selected");
    if (previousElement) {
      previousElement.classList.remove("selected");
    }

    let nearestElement = this.navAnchorTargets.filter(x => x.offsetParent !== null && x.getBoundingClientRect().top <= _SECTION_SCROLL_THRESHOLD_PIXELS).reverse()[0];
    if (!nearestElement) {
      // Fallback to first element if no nearest element within the threshold.
      nearestElement = this.navAnchorTargets[0];
    }

    this.exchangeTabsTarget.querySelector(`#${nearestElement.dataset.key}`)?.classList?.add("selected");
  }

  handleChartChange(e) {
    let buttonTarget = e.currentTarget;
    let chartTarget = e.currentTarget.closest(".chart-group").querySelector("div[data-controller='generic-chart']")

    chartTarget.dataset.dataUrl = buttonTarget.dataset.url;
    chartTarget.dataset.genericChartRerenderValue = true;
  }

  showExportDropdown(e) {
    if (!this.hasExportDropdownMenuTarget) {
      return;
    }

    e.stopPropagation();

    if (this.exportDropdownPopperInstance) {
      return this.hideExportDropdown(e);
    }

    this.exportButtonTarget.classList.add("selected");
    this.exportDropdownMenuTarget.classList.remove("tw-hidden");
    this.exportDropdownPopperInstance = createPopper(this.exportButtonTarget, this.exportDropdownMenuTarget, { placement: "bottom-start" });
  }

  hideExportDropdown(e) {
    if (!this.exportDropdownPopperInstance) {
      return;
    }

    this.exportButtonTarget.classList.remove("selected");
    this.exportDropdownMenuTarget.classList.add("tw-hidden");
    this.exportDropdownPopperInstance?.destroy();
    this.exportDropdownPopperInstance = null;
  }


  handleCalendarChange(selectedDates) {
    // Our Flatpickr is a range selector, so we need to check whether the returned array has two dates (to, from).
    if (!selectedDates || selectedDates.length !== 2 || this.isCalendarValid !== true) {
      return;
    }

    const [fromDate, toDate] = selectedDates;

    fromDate.setHours(0, 0, 0);
    toDate.setHours(23, 59, 59);

    this.calendarChartInstance.xAxis[0].setExtremes(fromDate.getTime(), toDate.getTime(), true, false, { trigger: "calendar" });
  }

  toggleCalendar(e) {
    if (this.calendarOpen === true) {
      this.calendarInstance.close();
    } else {
      this.calendarInstance.open();
    }
  }

  async refreshCalendarSettings() {
    if (!this.calendarInstance) {
      const Flatpickr = await loadFlatpickr();

      this.calendarInstance = Flatpickr(this.calendarButtonTarget, {
        position: "auto left",
        mode: "range",
        clickOpens: false,
        onChange: this.handleCalendarChange.bind(this),
        onOpen: () => {
          this.calendarButtonTarget.classList.add("selected");
          this.calendarOpen = true;
        },
        onClose: () => {
          this.calendarButtonTarget.classList.remove("selected");
          this.calendarOpen = false;
        }
      });
    }

    const mainDataAxis = this.calendarChartInstance.series[0]?.xAxis;
    const maxDataAxis = this.calendarChartInstance.navigator?.xAxis;
    if (!mainDataAxis || !maxDataAxis) {
      this.isCalendarValid = false;
      return;
    }

    const { min: selectedRangeFrom, max: selectedRangeTo } = mainDataAxis;
    const { min: allowedRangeFrom, max: allowedRangeTo } = maxDataAxis;

    this.calendarInstance.config.minDate = allowedRangeFrom;
    this.calendarInstance.config.maxDate = allowedRangeTo;
    this.calendarInstance.setDate([selectedRangeFrom, selectedRangeTo], false);
    this.isCalendarValid = true;
  }
}
